import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import type { InitConfig } from '../types';

type DatadogInitConfig = NonNullable<InitConfig['datadog']>;

export default {
  isInitialized: false,
  nestedSurface: '' as string,
  init(config: Omit<DatadogInitConfig, 'enabled'>): void {
    if (config.mode === 'nested') {
      this.nestedSurface = config.service;
      if (config.version) {
        datadogRum.setGlobalContextProperty('builderVersion', config.version);
      }
      this.isInitialized = true;
      return;
    }

    if (!config.applicationId || !config.clientToken) {
      return;
    }

    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      service: config.service,
      env: config.env,
      version: config.version,
      site: 'datadoghq.com',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      enablePrivacyForActionName: true,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackSessionAcrossSubdomains: config.trackSessionAcrossSubdomains,
      useSecureSessionCookie: true,
      useCrossSiteSessionCookie: config.useCrossSiteSessionCookie,
      traceSampleRate: 25,
      traceContextInjection: 'sampled',
      allowedTracingUrls: [window.location.origin],
      trackingConsent: config.trackingConsent ?? 'granted',
      silentMultipleInit: true
    });

    datadogLogs.init({
      clientToken: config.clientToken,
      service: config.service,
      env: config.env,
      version: config.version,
      site: 'datadoghq.com',
      sessionSampleRate: 100,
      trackSessionAcrossSubdomains: config.trackSessionAcrossSubdomains,
      useSecureSessionCookie: true,
      useCrossSiteSessionCookie: config.useCrossSiteSessionCookie,
      forwardErrorsToLogs: false,
      trackingConsent: config.trackingConsent ?? 'granted',
      silentMultipleInit: true
    });

    datadogRum.setGlobalContextProperty('surface', config.service);

    this.isInitialized = true;
  },
  identify(opaqueId: string): void {
    if (!this.isInitialized || !opaqueId) {
      return;
    }
    datadogRum.setUser({ id: opaqueId });
  },
  // Withdraw or grant RUM/log collection at runtime; Datadog stops/starts natively.
  setTrackingConsent(consent: 'granted' | 'not-granted'): void {
    if (!this.isInitialized) {
      return;
    }
    datadogRum.setTrackingConsent(consent);
    datadogLogs.setTrackingConsent(consent);
  },
  error(error: unknown): void {
    if (!this.isInitialized) {
      return;
    }

    const context = this.nestedSurface ? { surface: this.nestedSurface } : undefined;

    if (error instanceof Error) {
      datadogRum.addError(error, context);
      return;
    }

    if (typeof error === 'string') {
      datadogRum.addError(new Error(error), context);
      return;
    }

    try {
      datadogRum.addError(new Error(JSON.stringify(error)), context);
    } catch {
      datadogRum.addError(new Error(String(error ?? '')), context);
    }
  },
  info(message: string, context?: object): void {
    if (!this.isInitialized) {
      return;
    }
    const surfaceContext = this.nestedSurface ? { surface: this.nestedSurface } : undefined;
    datadogLogs.logger.info(message, { ...surfaceContext, ...context });
  },
  addFeatureFlag(flagName: string, variant: string): void {
    if (!this.isInitialized) {
      return;
    }
    datadogRum.addFeatureFlagEvaluation(flagName, variant);
  },
  addMetadata(key: string, value: any): void {
    if (!this.isInitialized) {
      return;
    }
    datadogRum.setGlobalContextProperty(key, value);
  }
};
